/**
 * Form1008Field Component
 *
 * A read-only field display component for Form 1008 data.
 * Supports various formats: currency, percentage, date, boolean, text.
 */

import { cn } from '@/lib/utils';
import { Check, X } from 'lucide-react';
import { parseAsLocalDate } from '@/utils/dateHelpers';

export type FieldFormat = 'text' | 'currency' | 'percentage' | 'date' | 'boolean' | 'number' | 'months';

interface Form1008FieldProps {
  label: string;
  value: string | number | boolean | null | undefined;
  format?: FieldFormat;
  className?: string;
  /** If true, displays as a compact inline field */
  inline?: boolean;
  /** If true, uses smaller text */
  compact?: boolean;
}

/**
 * Format a value based on the specified format type
 */
function formatValue(
  value: string | number | boolean | null | undefined,
  format: FieldFormat
): string {
  if (value === null || value === undefined || value === '') {
    return '--';
  }

  switch (format) {
    case 'currency':
      const numValue = typeof value === 'string' ? parseFloat(value.replace(/,/g, '')) : value;
      if (typeof numValue !== 'number' || isNaN(numValue)) {
return '--';
}
      return new Intl.NumberFormat('en-US', {
        style: 'currency',
        currency: 'USD',
        minimumFractionDigits: 2,
        maximumFractionDigits: 2
      }).format(numValue);

    case 'percentage':
      const pctValue = typeof value === 'string' ? parseFloat(value) : value;
      if (typeof pctValue !== 'number' || isNaN(pctValue)) {
return '--';
}
      return `${pctValue.toFixed(3)}%`;

    case 'date':
      if (typeof value !== 'string') {
return '--';
}
      try {
        const date = parseAsLocalDate(value);
        if (isNaN(date.getTime())) {
return '--';
}
        return date.toLocaleDateString('en-US', {
          month: '2-digit',
          day: '2-digit',
          year: 'numeric'
        });
      } catch {
        return '--';
      }

    case 'boolean':
      return typeof value === 'boolean' ? (value ? 'Yes' : 'No') : '--';

    case 'number':
      const num = typeof value === 'string' ? parseFloat(value) : value;
      if (typeof num !== 'number' || isNaN(num)) {
return '--';
}
      return num.toLocaleString('en-US');

    case 'months':
      const months = typeof value === 'string' ? parseInt(value, 10) : value;
      if (typeof months !== 'number' || isNaN(months)) {
return '--';
}
      return `${months} mo`;

    case 'text':
    default:
      return String(value);
  }
}

export function Form1008Field({
  label,
  value,
  format = 'text',
  className,
  inline = false,
  compact = false
}: Form1008FieldProps) {
  const formattedValue = formatValue(value, format);
  const isEmpty = formattedValue === '--';

  if (inline) {
    return (
      <div className={cn('flex items-center gap-2', className)}>
        <span className={cn('text-muted-foreground', compact ? 'text-xs' : 'text-sm')}>
          {label}:
        </span>
        <span
          className={cn(
            'font-medium',
            compact ? 'text-xs' : 'text-sm',
            isEmpty && 'text-muted-foreground'
          )}
        >
          {formattedValue}
        </span>
      </div>
    );
  }

  return (
    <div className={cn('space-y-1', className)}>
      <dt className={cn('text-muted-foreground', compact ? 'text-xs' : 'text-sm')}>
        {label}
      </dt>
      <dd
        className={cn(
          'font-medium',
          compact ? 'text-sm' : 'text-base',
          isEmpty && 'text-muted-foreground'
        )}
      >
        {formattedValue}
      </dd>
    </div>
  );
}

/**
 * A boolean field displayed as a checkbox-style indicator
 */
interface Form1008CheckFieldProps {
  label: string;
  checked: boolean | null | undefined;
  className?: string;
}

export function Form1008CheckField({ label, checked, className }: Form1008CheckFieldProps) {
  const isChecked = checked === true;

  return (
    <div className={cn('flex items-center gap-2', className)}>
      <div
        className={cn(
          'h-4 w-4 rounded border flex items-center justify-center',
          isChecked
            ? 'bg-primary border-primary'
            : 'bg-muted border-border'
        )}
      >
        {isChecked && <Check className="h-3 w-3 text-primary-foreground" />}
      </div>
      <span className="text-sm">{label}</span>
    </div>
  );
}

/**
 * A group of checkbox-style options (like Loan Type selector)
 */
interface Form1008OptionGroupProps {
  label: string;
  options: Array<{ value: string; label: string }>;
  selectedValue: string | null | undefined;
  className?: string;
}

export function Form1008OptionGroup({
  label,
  options,
  selectedValue,
  className
}: Form1008OptionGroupProps) {
  return (
    <div className={cn('space-y-2', className)}>
      <div className="text-sm text-muted-foreground">{label}</div>
      <div className="flex flex-wrap gap-3">
        {options.map((option) => (
          <Form1008CheckField
            key={option.value}
            label={option.label}
            checked={selectedValue === option.value}
          />
        ))}
      </div>
    </div>
  );
}
